import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAdminAuth } from '../../lib/adminAuth';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ShieldCheck, Loader2, AlertCircle } from 'lucide-react';

const AdminLoginPage = () => {
  const { login, token } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/admin';

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  React.useEffect(() => { if (token) navigate(from, { replace: true }); }, [token, navigate, from]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setSubmitting(true);
    try {
      await login(username.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#9a1b2f] via-[#7a1524] to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8 text-white">
          <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-[#9a1b2f]" strokeWidth={2.5} />
          </div>
          <span className="text-2xl font-bold">SafeHouse</span>
        </Link>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="mb-6">
            <h1 className="text-2xl font-black text-gray-900">Admin Panel</h1>
            <p className="text-sm text-gray-500 mt-1">Sign in to manage properties and view bookings.</p>
          </div>

          {error && (
            <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-100">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
              <div className="text-xs text-red-700">{error}</div>
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="u" className="text-xs">Username</Label>
              <Input id="u" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="admin" autoComplete="username" className="h-11 mt-1" />
            </div>
            <div>
              <Label htmlFor="p" className="text-xs">Password</Label>
              <Input id="p" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" className="h-11 mt-1" />
            </div>
            <Button type="submit" disabled={submitting} className="w-full h-11 bg-[#9a1b2f] hover:bg-[#7a1524]">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Sign In
            </Button>
          </form>

          <div className="mt-6 p-3 rounded-lg bg-gray-50 border border-gray-100 text-[11px] text-gray-500">
            <div className="font-semibold text-gray-700">Default credentials (change in production):</div>
            <div className="mt-1"><span className="font-mono">admin</span> / <span className="font-mono">admin123</span></div>
          </div>
        </div>

        <div className="text-center mt-6 text-xs text-white/70">
          <Link to="/" className="hover:text-white">← Back to homepage</Link>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
